import { hrefDoEmail, hrefDoTelefone, linkDeWhatsApp, saudacaoDeWhatsApp } from '@/content/contato';
import { useContato } from '@/hooks/use-conteudo';

/**
 * Rodapé do site.
 *
 * Contato, endereço e redes vêm de `content/contato.ts`, que é a mesma fonte
 * da seção do showroom — os dois mostram os mesmos dados, e mantê-los em dois
 * lugares garantiria que um dia divergissem.
 */

const NAVEGAR = [
  { id: 'atelie', rotulo: 'Ateliê' },
  { id: 'ambientes', rotulo: 'Ambientes' },
  { id: 'projetos', rotulo: 'Projetos' },
  { id: 'processo', rotulo: 'Processo' },
  { id: 'showroom', rotulo: 'Showroom' },
  { id: 'orcamento', rotulo: 'Orçamento' },
] as const;

function Coluna({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-[0.64rem] tracking-[0.18em] text-brass uppercase">{titulo}</h2>
      {children}
    </div>
  );
}

export function SiteFooter() {
  const contato = useContato();

  return (
    <footer className="mt-auto bg-brand-950 text-bone">
      <div className="mx-auto grid max-w-[1360px] gap-10 px-6 py-[clamp(48px,7vw,88px)] sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-3">
          <span className="flex flex-col leading-tight">
            <strong className="font-display text-base tracking-[0.14em]">PLANE</strong>
            <em className="text-[0.6rem] tracking-[0.16em] text-brand-150 not-italic uppercase">
              marcenaria &amp; planejados
            </em>
          </span>
          <p className="max-w-[28ch] text-sm text-brand-150">
            Cada milímetro pensado para o seu espaço.
          </p>
        </div>

        <Coluna titulo="Navegar">
          <ul className="flex flex-col gap-1.5 text-sm">
            {NAVEGAR.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className="text-brand-150 transition-colors hover:text-bone"
                >
                  {item.rotulo}
                </a>
              </li>
            ))}
          </ul>
        </Coluna>

        <Coluna titulo="Contato">
          <ul className="flex flex-col gap-1.5 text-sm text-brand-150">
            <li>
              <a href={hrefDoTelefone(contato)} className="transition-colors hover:text-bone">
                {contato.telefoneExibicao}
              </a>
            </li>
            <li>
              {/*
                O rótulo é "WhatsApp", mas o link precisa ir para o celular: o
                fixo logo acima não recebe mensagem, e apontar o wa.me para ele
                abriria uma conversa que não chega em ninguém.
              */}
              <a
                href={linkDeWhatsApp(contato, saudacaoDeWhatsApp(contato))}
                className="transition-colors hover:text-bone"
                rel="noreferrer noopener"
                target="_blank"
              >
                WhatsApp
              </a>
            </li>
            <li>
              <a href={hrefDoEmail(contato)} className="transition-colors hover:text-bone">
                {contato.email}
              </a>
            </li>
          </ul>
        </Coluna>

        <Coluna titulo="Endereço">
          <address className="flex flex-col gap-1.5 text-sm text-brand-150 not-italic">
            <span>
              {contato.enderecoLinha1}
              <br />
              {contato.enderecoLinha2}
            </span>
            <span>
              {contato.horarioSemanaCurto}
              <br />
              {contato.horarioSabadoCurto}
            </span>
          </address>

          <ul className="mt-2 flex gap-4 text-sm">
            {contato.redes.map((rede) => (
              <li key={rede.rotulo}>
                <a
                  href={rede.url}
                  rel="noreferrer noopener"
                  target="_blank"
                  className="text-brand-150 transition-colors hover:text-bone"
                >
                  {rede.rotulo}
                </a>
              </li>
            ))}
          </ul>
        </Coluna>
      </div>

      <div className="border-t border-bone/15">
        <div className="mx-auto flex max-w-[1360px] flex-col gap-1 px-6 py-6 text-xs text-brand-150 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} Plane Móveis Planejados · CNPJ {contato.cnpj}
          </p>
          <p>Feito com serragem e capricho.</p>
        </div>
      </div>
    </footer>
  );
}
